import type { ColumnDef } from '@tanstack/react-table';
import {
  Clock,
  Loader2,
  Package,
  TrendingDown,
  TrendingUp,
  Calendar,
  X,
} from 'lucide-react';
import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { DataTable } from '@/UI/TableShadcn';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

import type { IItemPurchaseHistory } from '../../../api/interfaces/ItemPurchaseHistory.interface';
import { getItemPurchaseHistory } from '../../../api/OffersRequest.api';

// Simple date formatter until utils are available
const formatDateToArabic = (dateString: string): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return new Intl.DateTimeFormat('ar-EG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Pre-defined array of Arabic months to avoid recreation
const ARABIC_MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'إبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

// Get month name in Arabic
const getArabicMonthName = (date: Date): string => {
  return ARABIC_MONTHS[date.getMonth()];
};

interface PurchaseHistoryDialogProps {
  item: IOneItemInPrepareForRequest | null;
  isOpen: boolean;
  closeFn: () => void;
}

// Optimized Monthly Chart component
const MonthlyQuantityChart = memo(
  ({
    purchaseHistory,
    className,
  }: {
    purchaseHistory: IItemPurchaseHistory[];
    className?: string;
  }) => {
    // Process data for the chart
    const chartData = useMemo(() => {
      if (!purchaseHistory.length) {
        return [];
      }

      const months: Record<string, { month: string; qty: number; date: Date }> =
        {};

      purchaseHistory.forEach((item) => {
        const date = new Date(item.TrDate);
        const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`;

        if (!months[monthYear]) {
          months[monthYear] = {
            month: `${getArabicMonthName(date)} ${date.getFullYear()}`,
            qty: 0,
            date: new Date(date.getFullYear(), date.getMonth(), 1),
          };
        }

        months[monthYear].qty += item.Qty;
      });

      // Sort by date
      const sortedData = Object.values(months).sort(
        (a, b) => a.date.getTime() - b.date.getTime(),
      );

      return sortedData;
    }, [purchaseHistory]);

    const chartColor = '#4f46e5'; // indigo-600

    // Custom tooltip component for Recharts
    const CustomTooltip = ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="rounded-md border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400">
              {label}
            </div>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
              الكمية:{' '}
              <span className="font-bold">
                {formatNumberWithCommas(payload[0].value)}
              </span>
            </div>
          </div>
        );
      }
      return null;
    };

    if (chartData.length === 0) {
      return (
        <div
          className={`flex h-64 items-center justify-center rounded-md border border-gray-800 bg-gray-950 p-4 text-gray-400 ${className}`}
        >
          لا توجد بيانات كافية للرسم البياني
        </div>
      );
    }

    // Current value (last data point)
    const currentValue = chartData[chartData.length - 1].qty;
    // First data point to calculate change
    const firstValue = chartData[0].qty;
    const valueChange = currentValue - firstValue;
    const percentChange = firstValue > 0 ? (valueChange / firstValue) * 100 : 0;

    // Determine if trending up or down
    const isTrendingUp = valueChange >= 0;
    const changeColor = isTrendingUp ? 'text-green-400' : 'text-red-400';

    return (
      <div
        className={`rounded-md border border-gray-800 bg-gray-950 p-4 ${className}`}
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-lg font-semibold text-white">
              {formatNumberWithCommas(currentValue)}
            </span>
            <span className={`text-sm ${changeColor}`}>
              {isTrendingUp ? '+' : ''}
              {formatNumberWithCommas(valueChange)} ({isTrendingUp ? '+' : ''}
              {percentChange.toFixed(2)}%)
            </span>
          </div>
          <div className="text-xs text-gray-400">
            آخر تحديث: {chartData[chartData.length - 1].month}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorQuantity" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColor} stopOpacity={0.9} />
                <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              hide={true}
              axisLine={false}
              tickLine={false}
            />
            <YAxis hide={true} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="qty"
              stroke={chartColor}
              strokeWidth={3}
              fill="url(#colorQuantity)"
              dot={{
                fill: chartColor,
                strokeWidth: 2,
                stroke: chartColor,
                r: 4,
              }}
              activeDot={{
                r: 7,
                stroke: chartColor,
                strokeWidth: 2,
                fill: chartColor,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

MonthlyQuantityChart.displayName = 'MonthlyQuantityChart';

// Memoized SummaryCard component
const SummaryCard = memo(
  ({
    title,
    value,
    icon,
    gradientFrom,
    suffix = '',
  }: {
    title: string;
    value: number | string;
    icon: React.ReactNode;
    gradientFrom: string;
    suffix?: string;
  }) => {
    // Map color schemes for different card types
    const getCardStyle = () => {
      const styles: Record<
        string,
        { light: string; dark: string; iconLight: string; iconDark: string }
      > = {
        'blue-500/90': {
          light: 'from-blue-400 to-indigo-500 shadow-blue-500/20',
          dark: 'dark:from-blue-600 dark:to-indigo-700 dark:shadow-blue-700/30',
          iconLight: 'bg-gradient-to-br from-white to-blue-100 text-blue-600',
          iconDark:
            'dark:bg-gradient-to-br dark:from-blue-900 dark:to-indigo-950 dark:text-blue-200',
        },
        'emerald-500/90': {
          light: 'from-emerald-400 to-green-500 shadow-emerald-500/20',
          dark: 'dark:from-emerald-600 dark:to-green-700 dark:shadow-emerald-700/30',
          iconLight:
            'bg-gradient-to-br from-white to-emerald-100 text-emerald-600',
          iconDark:
            'dark:bg-gradient-to-br dark:from-emerald-900 dark:to-green-950 dark:text-emerald-200',
        },
        'amber-500/90': {
          light: 'from-amber-400 to-yellow-500 shadow-amber-500/20',
          dark: 'dark:from-amber-600 dark:to-yellow-700 dark:shadow-amber-700/30',
          iconLight: 'bg-gradient-to-br from-white to-amber-100 text-amber-600',
          iconDark:
            'dark:bg-gradient-to-br dark:from-amber-900 dark:to-yellow-950 dark:text-amber-200',
        },
        'purple-500/90': {
          light: 'from-purple-400 to-fuchsia-500 shadow-purple-500/20',
          dark: 'dark:from-purple-600 dark:to-fuchsia-700 dark:shadow-purple-700/30',
          iconLight:
            'bg-gradient-to-br from-white to-purple-100 text-purple-600',
          iconDark:
            'dark:bg-gradient-to-br dark:from-purple-900 dark:to-fuchsia-950 dark:text-purple-200',
        },
      };

      return styles[gradientFrom] || styles['blue-500/90'];
    };

    const cardStyle = getCardStyle();

    return (
      <Card className="group relative overflow-hidden rounded-xl border-0 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:bg-gray-900/40">
        {/* Card background glow effect on hover */}
        <div
          className={`absolute inset-0 bg-gradient-to-r ${cardStyle.light} ${cardStyle.dark} opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-20 dark:group-hover:opacity-10`}
        />

        <CardContent className="p-0">
          <div
            className={`relative overflow-hidden bg-gradient-to-br p-5 text-white ${cardStyle.light} ${cardStyle.dark}`}
          >
            <div className="flex items-center justify-between">
              <div className="z-10 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wider text-white/80 dark:text-white/70">
                  {title}
                </p>
                <p className="text-2xl font-bold tracking-tight text-white drop-shadow-sm">
                  {typeof value === 'number'
                    ? formatNumberWithCommas(value)
                    : value}
                  <span className="ml-0.5">{suffix}</span>
                </p>
              </div>

              <div
                className={`z-10 flex h-12 w-12 items-center justify-center rounded-full shadow-md backdrop-blur-sm transition-transform duration-300 group-hover:scale-110 ${cardStyle.iconLight} ${cardStyle.iconDark}`}
              >
                {icon}
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white/5 blur-2xl" />

            {/* Shimmer effect on hover */}
            <div className="absolute inset-0 translate-x-[-100%] skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:animate-shimmer" />
          </div>
        </CardContent>
      </Card>
    );
  },
);

SummaryCard.displayName = 'SummaryCard';

function PurchaseHistoryDialog({
  item,
  isOpen,
  closeFn,
}: PurchaseHistoryDialogProps) {
  const [purchaseHistory, setPurchaseHistory] = useState<
    IItemPurchaseHistory[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  // Optimized data fetching function with useCallback
  const fetchPurchaseHistory = useCallback(async (itemId: number) => {
    setIsLoading(true);
    try {
      const data = await getItemPurchaseHistory(itemId);
      setPurchaseHistory(data);
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Only fetch data when necessary
  useEffect(() => {
    if (isOpen && item?.HealthyItemID) {
      fetchPurchaseHistory(item.HealthyItemID);
    }

    // Clear data when dialog closes to free memory
    return () => {
      if (!isOpen) {
        setPurchaseHistory([]);
      }
    };
  }, [isOpen, item?.HealthyItemID, fetchPurchaseHistory]);

  // Calculate summary data with optimized memoization
  const summaryData = useMemo(() => {
    if (!purchaseHistory.length)
      return {
        totalQty: 0,
        bestDiscount: 0,
        minDiscount: 0,
        monthlyAverage: 0,
      };

    let totalQty = 0;
    let bestDiscount = 0;
    let minDiscount = Infinity;

    // Get unique months for monthly average calculation
    const monthsMap = new Map();

    purchaseHistory.forEach((item) => {
      totalQty += item.Qty;
      bestDiscount = Math.max(bestDiscount, item.Discount);
      if (item.Discount > 0) {
        minDiscount = Math.min(minDiscount, item.Discount);
      }

      // Track unique months for average calculation - use efficient date operations
      const date = new Date(item.TrDate);
      const monthYear = `${date.getFullYear()}-${date.getMonth() + 1}`;

      if (!monthsMap.has(monthYear)) {
        monthsMap.set(monthYear, { totalQty: 0 });
      }

      monthsMap.get(monthYear).totalQty += item.Qty;
    });

    // Calculate monthly average
    const monthlyAverage =
      monthsMap.size > 0 ? Math.round(totalQty / monthsMap.size) : 0;

    return {
      totalQty,
      bestDiscount,
      minDiscount: minDiscount === Infinity ? 0 : minDiscount,
      monthlyAverage,
    };
  }, [purchaseHistory]);

  // Define columns for the table - memoized to prevent recreation
  const columns = useMemo<ColumnDef<IItemPurchaseHistory>[]>(
    () => [
      {
        accessorKey: 'TrDate',
        header: () => (
          <div className="flex items-center justify-center gap-1 text-gray-700 dark:text-gray-300">
            <Calendar className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
            <span>التاريخ</span>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <Badge
              variant="outline"
              className="bg-indigo-50/50 px-2 py-1 shadow-sm transition-colors hover:bg-indigo-100/50 dark:bg-indigo-950/30 dark:hover:bg-indigo-900/40"
            >
              <Clock className="mr-1 h-3 w-3 text-indigo-500 dark:text-indigo-400" />
              <span className="font-medium text-indigo-700 dark:text-indigo-300">
                {formatDateToArabic(row.original.TrDate)}
              </span>
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: 'Name',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            اسم المورد
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1 py-1.5 text-center">
            <div className="rounded-md bg-blue-50/30 px-2 py-1.5 font-medium text-blue-800 transition-colors hover:bg-blue-50/60 dark:bg-blue-950/20 dark:text-blue-300 dark:hover:bg-blue-900/30">
              {row.original.Name}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'Qty',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            الكمية
          </div>
        ),
        cell: ({ row }) => (
          <div className="rounded-md bg-gray-50/50 px-3 py-1.5 text-center font-semibold text-gray-700 shadow-sm dark:bg-gray-800/50 dark:text-gray-300">
            {formatNumberWithCommas(row.original.Qty)}
          </div>
        ),
      },
      {
        accessorKey: 'UnitPrice',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            سعر الوحدة
          </div>
        ),
        cell: ({ row }) => (
          <div className="rounded-md bg-emerald-50/40 px-3 py-1.5 text-center font-medium text-emerald-700 shadow-sm dark:bg-emerald-950/20 dark:text-emerald-300">
            {formatNumberWithCommas(row.original.UnitPrice)}
            <span className="mr-1 text-xs font-normal text-emerald-600/70 dark:text-emerald-400/70">
              جنيه
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'Discount',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            الخصم
          </div>
        ),
        cell: ({ row }) => {
          const discount = row.original.Discount;
          const bgColor =
            discount > 0
              ? 'bg-green-50/50 text-green-700 dark:bg-green-950/30 dark:text-green-300'
              : 'bg-gray-100/50 text-gray-500 dark:bg-gray-800/30 dark:text-gray-400';

          return (
            <div
              className={`flex items-center justify-center gap-0.5 rounded-md px-3 py-1.5 font-medium shadow-sm ${bgColor}`}
            >
              {formatNumberWithCommas(discount)}
              <span className="text-xs">%</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'Tax',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            الضريبة
          </div>
        ),
        cell: ({ row }) => {
          const tax = row.original.Tax;
          const bgColor =
            tax > 0
              ? 'bg-amber-50/50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
              : 'bg-gray-100/50 text-gray-500 dark:bg-gray-800/30 dark:text-gray-400';

          return (
            <div
              className={`flex items-center justify-center gap-0.5 rounded-md px-3 py-1.5 font-medium shadow-sm ${bgColor}`}
            >
              {formatNumberWithCommas(tax)}
              <span className="text-xs">%</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'batch',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            الباتش
          </div>
        ),
        cell: ({ row }) => (
          <div className="mx-auto flex max-w-xs flex-col space-y-1 p-1">
            {row.original.BNO || row.original.ExpireDate ? (
              <>
                {row.original.BNO && (
                  <div className="flex items-center justify-center gap-1.5 rounded-md bg-blue-50/30 px-2 py-1 dark:bg-blue-950/20">
                    <Badge
                      variant="outline"
                      className="h-5 border-blue-200 bg-blue-50 px-1.5 text-xs font-medium text-blue-700 shadow-sm dark:border-blue-800/30 dark:bg-blue-950/30 dark:text-blue-300"
                    >
                      {row.original.BNO}
                    </Badge>
                  </div>
                )}
                {row.original.ExpireDate && (
                  <div className="flex items-center justify-center gap-1.5 rounded-md bg-amber-50/30 px-2 py-1 dark:bg-amber-950/20">
                    <Badge
                      variant="outline"
                      className="h-5 border-amber-200 bg-amber-50 px-1.5 text-xs font-medium text-amber-800 shadow-sm dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
                    >
                      {formatDateToArabic(row.original.ExpireDate)}
                    </Badge>
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-8 items-center justify-center text-center text-gray-400 dark:text-gray-600">
                -
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'Bonus',
        header: () => (
          <div className="text-center font-medium text-gray-700 dark:text-gray-300">
            البونص
          </div>
        ),
        cell: ({ row }) => {
          const bonus = row.original.Bonus;
          const hasBonus = bonus > 0;

          return (
            <div
              className={`rounded-md px-3 py-1.5 text-center font-medium shadow-sm ${
                hasBonus
                  ? 'bg-purple-50/50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300'
                  : 'bg-gray-100/50 text-gray-500 dark:bg-gray-800/30 dark:text-gray-400'
              }`}
            >
              {formatNumberWithCommas(row.original.Bonus)}
            </div>
          );
        },
      },
    ],
    [],
  );

  // Memoize the close handler
  const handleClose = useCallback(() => {
    closeFn();
  }, [closeFn]);

  if (!item) return null;

  // Optimized rendering with conditional content
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        size="5xl"
        className="p-0"
        hiddenCloseIcon
        overlayClass="custom-dialog-overlay"
        aria-describedby="purchase-history-dialog-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-teal-50 p-3 dark:from-blue-950/80 dark:to-teal-950/80">
            <div className="flex items-center gap-3.5">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 p-2 shadow-xl shadow-blue-300/20 dark:bg-blue-900/60 dark:shadow-blue-900/30">
                <Clock className="h-9 w-9 text-blue-600 dark:text-blue-300" />
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-semibold text-blue-700 dark:text-blue-300">
                  سجل المشتريات
                </span>
                <div className="mt-0.5 flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <span className="max-w-[240px] truncate">
                    {item.ClientItemName}
                  </span>
                </div>
              </div>
            </div>
            <DialogDescription className="hidden p-3 pt-0 text-sm text-gray-500 dark:text-gray-400">
              يعرض هذا التقرير تاريخ عمليات شراء المنتج &quot;
              {item?.ClientItemName || 'المحدد'}&quot; من الموردين، ويشمل
              الكميات الشهرية، وإجمالي المشتريات، وتفاصيل الأسعار والخصومات
              والباتشات. يمكنك الاطلاع على هذه البيانات لاتخاذ قرارات مستنيرة
              بشأن عمليات الشراء المستقبلية.
            </DialogDescription>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-10 w-10 rounded-full hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-900/40 dark:hover:text-blue-300"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="bg-white p-4 dark:bg-background">
          {isLoading ? (
            <div className="flex h-60 flex-col items-center justify-center gap-4">
              <div className="relative h-20 w-20">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-16 w-16 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              </div>
              <div className="text-center">
                <div className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
                  جاري تحميل البيانات
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  يرجى الانتظار قليلاً...
                </div>
              </div>
            </div>
          ) : purchaseHistory.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-gray-500 dark:text-gray-400">
              لا يوجد سجل مشتريات لهذا المنتج
            </div>
          ) : (
            <>
              {/* Main grid layout - optimized with CSS Grid */}
              <div className="mb-6 grid grid-cols-[400px_1fr] gap-4">
                {/* Left column with chart and cards */}
                <div className="space-y-4">
                  {/* Chart at top of left column */}
                  <Card className="overflow-hidden shadow-sm">
                    <CardContent className="p-0">
                      <MonthlyQuantityChart
                        purchaseHistory={purchaseHistory}
                        className="w-full"
                      />
                    </CardContent>
                  </Card>

                  {/* Cards stacked under chart - now using reusable components */}
                  <SummaryCard
                    title="إجمالي الكميات"
                    value={summaryData.totalQty}
                    icon={<Package className="h-5 w-5" />}
                    gradientFrom="blue-500/90"
                    suffix="كمية"
                  />

                  <SummaryCard
                    title="أعلى خصم"
                    value={summaryData.bestDiscount}
                    icon={<TrendingUp className="h-5 w-5" />}
                    gradientFrom="emerald-500/90"
                    suffix="%"
                  />

                  <SummaryCard
                    title="أقل خصم"
                    value={summaryData.minDiscount}
                    icon={<TrendingDown className="h-5 w-5" />}
                    gradientFrom="amber-500/90"
                    suffix="%"
                  />

                  <SummaryCard
                    title="متوسط الشراء الشهري"
                    value={summaryData.monthlyAverage}
                    icon={<Calendar className="h-5 w-5" />}
                    gradientFrom="purple-500/90"
                    suffix="كمية"
                  />
                </div>

                {/* Right column for table - optimized with fixed sizing */}
                <div className="h-full">
                  <div className="h-full overflow-hidden rounded-md border shadow-sm">
                    <DataTable
                      columns={columns}
                      data={purchaseHistory}
                      isPaginated
                      pageSize={30}
                      heightTable="70vh"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default memo(PurchaseHistoryDialog);
